/* eslint-disable react/no-array-index-key */
/* eslint-disable no-nested-ternary */

import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Formik, FieldArray } from 'formik';
import classnames from 'classnames';
import {
  Row,
  Col,
  Card,
  CardBody,
  CardFooter,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
} from 'reactstrap';
import { Button, Field, FormAlerts, CardTitle } from '@cordata/buffet';
import { orderBy, map, compact, startCase } from 'lodash';
import { Link, withRouter, useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';
import * as Yup from 'yup';

import {
  referenceAdminInstance as refAdmin,
  adminInstanceRaw,
  toastSuccess,
} from '~/utils';

import useSlice from '~/utils/slice/hook';
import SliceFields from '~/components/Slice/Fields';

import FilterForm from './FilterForm';
import TableForm from './TableForm';
import ChartForm, { dataLabelValueOptions } from './ChartForm';
import LagTableForm from './LagTableForm';
import LayoutEditor from './LayoutEditor';

const COLUMN_TYPE_MAP = {
  dynamic: null,
  date: /date|timestamp/i,
  time: /date|timestamp/i,
  dateTime: /date|timestamp/i,
  boolean: /boolean/i,
  number: /int|numeric|double/i,
  array: /\[\]|array/i,
  string: /text|varchar/i,
  richtext: /text|varchar/i,
};

const ConfiguredReportsForm = ({ data }) => {
  const { serialize: serializeSlice } = useSlice();
  const history = useHistory();

  const [availableReports, setAvailableReports] = useState([]);
  const [availableRefDataModels, setAvailableRefDataModels] = useState([]);

  const [parsedJSON, setParsedJSON] = useState();

  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    if (data.attributes) {
      setParsedJSON(JSON.parse(data.attributes.reportConfigurationDefinition));
    }
  }, [data]);

  useEffect(() => {
    refAdmin
      .get('/available?sort=name')
      .then(r => r.data)
      .then(r => {
        const models = map(orderBy(Object.values(r), 'name'), v => ({
          label: v.name,
          value: v.name,
          labelAttr: v.labelAttr,
          nameAttr: v.nameAttr,
          typeAttr: v.typeAttr,
          availableTypes: v.availableTypes.map(t => ({
            label: t,
            value: t,
          })),
        }));
        setAvailableRefDataModels(models);
      });
  }, []);

  useEffect(() => {
    adminInstanceRaw
      .get('/pviews/available')
      .then(r => r.data.data)
      .then(r => {
        const reports = map(orderBy(Object.values(r), 'name'), v => ({
          label: v.name,
          value: v.name,
          columns: orderBy(
            map(v.fields, (vv, k) => {
              let matchedType;

              Object.keys(COLUMN_TYPE_MAP).forEach(type => {
                if (COLUMN_TYPE_MAP[type] && vv.type.match(COLUMN_TYPE_MAP[type])) {
                  matchedType = type;
                }
              });

              return {
                label: k,
                value: k,
                type: matchedType || 'string',
              };
            }),
            'label',
          ),
        }));

        setAvailableReports(reports);
      });
  }, []);

  const resizeHack = () => {
    // layout editor gets wrong width when loaded on non-initial tab

    setTimeout(() => {
      const resizeEvent = document.createEvent('Event');
      resizeEvent.initEvent('resize', false, false);
      window.dispatchEvent(resizeEvent);
    }, 200);
  };

  const getDefaultLayout = json => {
    // if layout exists, just return it as is
    // if no layout exists, create one, for backwards compatibility with
    // reports that existed pre-layouts

    if (!json) return {};

    if (json.layout) return json.layout;

    const layout = {
      filters: [],
      visualizations: [],
    };

    if (json.filters) {
      json.filters.forEach((f, i) => {
        layout.filters.push({
          i: `filter-${i}`,
          x: 0,
          y: i,
          w: 12,
          h: 1,
          maxH: 1,
        });
      });
    }

    if (json.charts) {
      json.charts.forEach((c, i) => {
        layout.visualizations.push({
          i: `chart-${i}`,
          x: 0,
          y: i,
          w: 12,
          h: 6,
          minW: 3,
          minH: 3,
        });
      });
    }

    if (json.tables) {
      json.tables.forEach((t, i) => {
        layout.visualizations.push({
          i: `table-${i}`,
          x: 0,
          y: i + (json.charts ? json.charts.length : 0),
          w: 12,
          h: 3,
          minW: 3,
          minH: 3,
        });
      });
    }

    if (json.lagTables) {
      json.lagTables.forEach((lt, i) => {
        layout.visualizations.push({
          i: `lagtable-${i}`,
          x: 0,
          y:
            i +
            (json.charts ? json.charts.length : 0) +
            (json.tables ? json.tables.length : 0),
          w: 12,
          h: 6,
          minW: 3,
          minH: 3,
        });
      });
    }

    return layout;
  };

  return (
    <Formik
      displayName="ConfiguredReportFormikEnhancer"
      validateOnChange={false}
      validateOnBlur={false}
      enableReinitialize
      initialValues={{
        ...data,
        ...data.attributes,
        reportView:
          parsedJSON &&
          availableReports.find(r => r.value === parsedJSON.reportView),
        filters:
          parsedJSON &&
          parsedJSON.filters &&
          parsedJSON.filters.map(f => ({
            label: f.label,
            startDateLabel: f.startDateLabel,
            endDateLabel: f.endDateLabel,
            columnName: {
              label: f.columnName,
              value: f.columnName,
            },
            type: {
              label: f.type,
              value: f.type,
            },
            comparisonType: f.comparisonType,
            allowDecimals: f.allowDecimals,
            reviewersOnly: f.reviewersOnly,
            refDataModel: availableRefDataModels.find(t => t.value === f.refDataModel),
            refDataType: {
              label: f.refDataType,
              value: f.refDataType,
            },
            radioButtonTreatNullAsFalse: f.radioButtonTreatNullAsFalse,
            radioButtonOptionAll: f.radioButtonOptionAll,
            radioButtonOptionTrue: f.radioButtonOptionTrue,
            radioButtonOptionFalse: f.radioButtonOptionFalse,
            customDropdownOptions: f.customDropdownOptions,
            isCaseInsensitive: f.isCaseInsensitive,
            isMultiSelect: f.isMultiSelect,
            defaultStartDate: f.defaultStartDate,
            defaultEndDate: f.defaultEndDate,
            defaultValue: f.defaultValue,
          })),
        charts:
          parsedJSON &&
          parsedJSON.charts &&
          parsedJSON.charts.map(c => ({
            title: c.title,
            type: {
              label: c.type,
              value: c.type,
            },
            isTimeSeries: c.isTimeSeries,
            reportView:
              c.reportView &&
              availableReports.find(r => r.value === c.reportView),
            usesSameView: !c.reportView,
            columnName: {
              label: c.columnName,
              value: c.columnName,
            },
            latitudeColumn: {
              label: c.latitudeColumn,
              value: c.latitudeColumn,
            },
            longitudeColumn: {
              label: c.longitudeColumn,
              value: c.longitudeColumn,
            },
            xAxisColumn: c.xAxisColumn,
            xAxisColumnDBFormat: c.xAxisColumnDBFormat,
            yAxisColumn: c.yAxisColumn,
            xTickFormat: c.xTickFormat,
            yTickFormat: {
              label: c.yTickFormat,
              value: c.yTickFormat,
            },
            yTickMin: c.yTickMin,
            yTickMax: c.yTickMax,
            aggregationType: c.aggregationType
              ? {
                  label: startCase(c.aggregationType),
                  value: c.aggregationType,
                }
              : c.shouldAggregateData
              ? {
                  // update old reports to new config format
                  // shouldAggregateData is now aggregationType of 'sum'
                  label: 'Sum',
                  value: 'sum',
                }
              : null,
            isStacked: c.isStacked,
            isFilledLine: c.isFilledLine,
            timeUnit: c.timeUnit,
            shouldShowLegend: c.shouldShowLegend,
            legendPosition: {
              label: c.legendPosition,
              value: c.legendPosition,
            },
            showNulls: c.showNulls === undefined ? true : c.showNulls,
            nullText: c.nullText === undefined ? 'Unknown' : c.nullText,
            displayDataLabels:
              c.displayDataLabels === undefined ? true : c.displayDataLabels,
            dataLabelValue: c.dataLabelValue
              ? dataLabelValueOptions.find(o => o.value === c.dataLabelValue)
              : c.type === 'pie' || c.type === 'doughnut'
              ? dataLabelValueOptions.find(o => o.value === 'percentage')
              : dataLabelValueOptions.find(o => o.value === 'value'),
            categoryOrder: c.categoryOrder || [],
          })),
        tables:
          parsedJSON &&
          parsedJSON.tables &&
          parsedJSON.tables.map(t => ({
            title: t.title,
            reportView:
              t.reportView &&
              availableReports.find(r => r.value === t.reportView),
            pageSize: t.pageSize,
            groupByColumn: t.groupByColumn && {
              label: t.groupByColumn,
              value: t.groupByColumn,
            },
            groupByDisplayColumns:
              t.groupByDisplayColumns &&
              t.groupByDisplayColumns.map(c => ({
                label: c,
                value: c,
              })),

            sortBy:
              t.sortBy &&
              t.sortBy.map(c => ({
                label: c.columnName,
                value: c.columnName,
                desc: c.desc,
              })),
            columns: t.columns.map(c => {
              if (c.pivot) {
                return {
                  isPivot: true,
                  keyColumn: {
                    label: c.pivot.keyColumn,
                    value: c.pivot.keyColumn,
                  },
                  valueColumn: {
                    label: c.pivot.valueColumn,
                    value: c.pivot.valueColumn,
                  },
                  type: {
                    label: startCase(c.pivot.type),
                    value: c.pivot.type,
                  },
                };
              }
              return {
                columnName: {
                  label: c.columnName,
                  value: c.columnName,
                },
                displayHeader: c.displayHeader,
                type: {
                  label: startCase(c.type),
                  value: c.type,
                },
                aggregationType: {
                  label: startCase(c.aggregationType),
                  value: c.aggregationType,
                },
              };
            }),
            fixHeight: t.fixHeight,
          })),
        lagTables:
          parsedJSON &&
          parsedJSON.lagTables &&
          parsedJSON.lagTables.map(lt => ({
            title: lt.title,
            reportView:
              lt.reportView &&
              availableReports.find(r => r.value === lt.reportView),
            pageSize: lt.pageSize || 500,
            groupByColumns:
              lt.groupByColumns &&
              lt.groupByColumns.map(col => ({
                label: col,
                value: col,
              })),
            divisionNameColumn: lt.divisionNameColumn && {
              label: lt.divisionNameColumn,
              value: lt.divisionNameColumn,
            },
            lagCalculations:
              lt.lagCalculations &&
              lt.lagCalculations.map(lc => ({
                label: lc.label,
                startDateColumn: lc.startDateColumn && {
                  label: lc.startDateColumn,
                  value: lc.startDateColumn,
                },
                endDateColumn: lc.endDateColumn && {
                  label: lc.endDateColumn,
                  value: lc.endDateColumn,
                },
                businessDaysMode: lc.businessDaysMode && {
                  label: startCase(lc.businessDaysMode),
                  value: lc.businessDaysMode,
                },
                businessDaysColumn: lc.businessDaysColumn && {
                  label: lc.businessDaysColumn,
                  value: lc.businessDaysColumn,
                },
              })),
            additionalCountColumns:
              lt.additionalCountColumns &&
              lt.additionalCountColumns.map(cc => ({
                label: cc.label,
                countWhenColumn: cc.countWhenColumn && {
                  label: cc.countWhenColumn,
                  value: cc.countWhenColumn,
                },
                countCondition: cc.countCondition && {
                  label: startCase(cc.countCondition),
                  value: cc.countCondition,
                },
                countValue: cc.countValue,
              })),
            columns:
              lt.columns &&
              lt.columns.map(col => ({
                type: col.type && {
                  label: startCase(col.type),
                  value: col.type,
                },
                aggregationType: col.aggregationType && {
                  label: startCase(col.aggregationType),
                  value: col.aggregationType,
                },
                sourceKey: col.sourceKey && {
                  label: col.sourceKey,
                  value: col.sourceKey,
                },
                displayHeader: col.displayHeader,
                showCustomNullText: col.showCustomNullText || false,
                nullText: col.nullText || '',
              })),
          })),
        layout: getDefaultLayout(parsedJSON),
      }}
      validationSchema={Yup.object().shape({
        reportConfigurationName: Yup.string()
          .matches(/^\S*$/, { message: 'Cannot contain spaces' })
          .required(),
        reportConfigurationDescription: Yup.string().nullable(),
        reportView: Yup.string().required(),
        filters: Yup.array().of(Yup.object().shape({
            label: Yup.string().nullable(),
            startDateLabel: Yup.string().nullable(),
            endDateLabel: Yup.string().nullable(),
            columnName: Yup.string().required(),
            type: Yup.string().required(),
            comparisonType: Yup.string().nullable(),
            allowDecimals: Yup.boolean(),
            reviewersOnly: Yup.boolean(),
            refDataModel: Yup.string().nullable(),
            refDataType: Yup.string().nullable(),
            refDataLabelAttr: Yup.string().nullable(),
            radioButtonTreatNullAsFalse: Yup.string().nullable(),
            radioButtonOptionAll: Yup.string().nullable(),
            radioButtonOptionTrue: Yup.string().nullable(),
            radioButtonOptionFalse: Yup.string().nullable(),
            customDropdownOptions: Yup.array().of(Yup.object().shape()),
            isCaseInsensitive: Yup.boolean(),
            isMultiSelect: Yup.boolean(),
            defaultStartDate: Yup.string().nullable(),
            defaultEndDate: Yup.string().nullable(),
            defaultValue: Yup.string().nullable(),
          })),
        charts: Yup.array().of(Yup.object().shape({
            title: Yup.string().nullable(),
            type: Yup.string().required(),
            isTimeSeries: Yup.boolean(),
            timeUnit: Yup.object(),
            reportView: Yup.string().nullable(),
            columnName: Yup.string().nullable(),
            yAxisColumn: Yup.array().of(Yup.object().shape({
                label: Yup.string().required(),
                value: Yup.string().required(),
              })),
            xAxisColumn: Yup.string().nullable(),
            xAxisColumnDBFormat: Yup.string().nullable(),
            xTickFormat: Yup.string().nullable(),
            yTickFormat: Yup.string().nullable(),
            yTickMin: Yup.number(),
            yTickMax: Yup.number(),
            aggregationType: Yup.string().nullable(),
            isStacked: Yup.boolean(),
            isFilledLine: Yup.boolean(),
            shouldShowLegend: Yup.boolean(),
            legendPosition: Yup.string().nullable(),
            showNulls: Yup.boolean(),
            nullText: Yup.string().nullable(),
            displayDataLabels: Yup.boolean(),
            dataLabelValue: Yup.string().nullable(),
          })),
        tables: Yup.array().of(Yup.object().shape({
            title: Yup.string().nullable(),
            reportView: Yup.string().nullable(),
            pageSize: Yup.number().required(),
            groupByColumn: Yup.string().nullable(),
            sortBy: Yup.array().nullable(),
            columns: Yup.array()
              .of(Yup.object().shape({
                  columnName: Yup.string().nullable(),
                  displayHeader: Yup.string().nullable(),
                  type: Yup.string().nullable(),
                  aggregationType: Yup.string().nullable(),
                  isPivot: Yup.boolean().nullable(),
                  keyColumn: Yup.string().nullable(),
                  valueColumn: Yup.string().nullable(),
                }))
              .min(1, args => `table must have at least ${args.min} column`),
            fixHeight: Yup.boolean(),
          })),
        lagTables: Yup.array().of(Yup.object().shape({
            title: Yup.string().nullable(),
            reportView: Yup.string().nullable(),
            pageSize: Yup.number().required(),
            groupByColumns: Yup.array().min(
              1,
              'At least one group by column is required',
            ),
            divisionNameColumn: Yup.string().nullable(),
            lagCalculations: Yup.array().of(Yup.object().shape({
                label: Yup.string().required(),
                startDateColumn: Yup.string().required(),
                endDateColumn: Yup.string().required(),
                businessDaysMode: Yup.string().nullable(),
                businessDaysColumn: Yup.string().nullable(),
              })),
            additionalCountColumns: Yup.array().of(Yup.object().shape({
                label: Yup.string().required(),
                countWhenColumn: Yup.string().required(),
                countCondition: Yup.string().required(),
                countValue: Yup.string().nullable(),
              })),
            columns: Yup.array()
              .of(Yup.object().shape({
                  type: Yup.string().required(),
                  aggregationType: Yup.string().nullable(),
                  sourceKey: Yup.string().required(),
                  displayHeader: Yup.string().required(),
                }))
              .min(1, args => `lag table must have at least ${args.min} column`),
          })),
        layout: Yup.object(),
      })}
      onSubmit={(values, { setErrors, setSubmitting }) => {
        const method = values.id ? 'put' : 'post';
        const url = values.id
          ? `/reference-report-configuration/${values.id}`
          : '/reference-report-configuration';

        if (
          (values.tables || []).length +
            (values.charts || []).length +
            (values.lagTables || []).length <
          1
        ) {
          setErrors({ server: 'need at least one table, chart, or lag table' });
        }

        const getDefaultValue = f => {
          if (f.type.value === 'number') {
            return f.defaultValue;
          }

          if (f.type.value === 'string') {
            return f.defaultValue;
          }

          if (f.isMultiSelect || f.type.value === 'badge') {
            if (Array.isArray(f.defaultValue)) {
              return compact(f.defaultValue).map(v => v.value);
            }

            return f.defaultValue && [f.defaultValue.value];
          }

          if (Array.isArray(f.defaultValue)) {
            return f.defaultValue[0] && f.defaultValue[0].value;
          }

          return f.defaultValue && f.defaultValue.value;
        };

        const reportConfigurationDefinition = JSON.stringify({
          reportView: values.reportView.value,
          filters:
            values.filters &&
            values.filters.map(f => ({
              label: f.label,
              startDateLabel: f.startDateLabel,
              endDateLabel: f.endDateLabel,
              columnName: f.columnName.value,
              type: f.type.value,
              comparisonType: f.comparisonType,
              allowDecimals: f.allowDecimals,
              reviewersOnly: f.reviewersOnly,
              refDataModel: f.refDataModel && f.refDataModel.value,
              refDataType: f.refDataType && f.refDataType.value,
              refDataLabelAttr: f.refDataModel && f.refDataModel.labelAttr,
              radioButtonTreatNullAsFalse: f.radioButtonTreatNullAsFalse,
              radioButtonOptionAll: f.radioButtonOptionAll,
              radioButtonOptionTrue: f.radioButtonOptionTrue,
              radioButtonOptionFalse: f.radioButtonOptionFalse,
              customDropdownOptions: f.customDropdownOptions,
              isCaseInsensitive: f.isCaseInsensitive,
              isMultiSelect: f.isMultiSelect,
              defaultStartDate: f.defaultStartDate && f.defaultStartDate.value,
              defaultEndDate: f.defaultEndDate && f.defaultEndDate.value,
              defaultValue: getDefaultValue(f),
            })),
          charts:
            values.charts &&
            values.charts.map(c => ({
              title: c.title,
              type: c.type.value,
              isTimeSeries: c.isTimeSeries,
              timeUnit: c.timeUnit,
              reportView: c.usesSameView
                ? null
                : c.reportView && c.reportView.value,
              columnName: c.columnName && c.columnName.value,
              latitudeColumn: c.latitudeColumn && c.latitudeColumn.value,
              longitudeColumn: c.longitudeColumn && c.longitudeColumn.value,
              xAxisColumn: c.xAxisColumn,
              xAxisColumnDBFormat: c.xAxisColumnDBFormat,
              yAxisColumn: c.yAxisColumn,
              xTickFormat: c.xTickFormat,
              yTickFormat: c.yTickFormat && c.yTickFormat.value,
              yTickMin: c.yTickMin,
              yTickMax: c.yTickMax,
              aggregationType: c.aggregationType && c.aggregationType.value,
              isStacked: c.isStacked,
              isFilledLine: c.isFilledLine,
              shouldShowLegend: c.shouldShowLegend,
              legendPosition: c.legendPosition && c.legendPosition.value,
              showNulls: c.showNulls,
              nullText: c.nullText,
              displayDataLabels: c.displayDataLabels,
              dataLabelValue: c.dataLabelValue && c.dataLabelValue.value,
              categoryOrder: c.categoryOrder && c.categoryOrder.length
                ? c.categoryOrder
                : undefined,
            })),
          tables:
            values.tables &&
            values.tables.map(t => ({
              title: t.title,
              reportView: t.reportView && t.reportView.value,
              pageSize: t.pageSize,
              groupByColumn: t.groupByColumn && t.groupByColumn.value,
              groupByDisplayColumns:
                t.groupByDisplayColumns && t.groupByDisplayColumns.map(c => c.value),

              sortBy:
                t.sortBy &&
                t.sortBy.map(c => ({
                  columnName: c.value,
                  desc: !!c.desc,
                })),
              columns: t.columns
                .filter(c => (c.isPivot ? (c.keyColumn && c.valueColumn) : c.columnName))
                .map(c => {
                if (c.isPivot) {
                  return {
                    pivot: {
                      keyColumn: c.keyColumn.value,
                      valueColumn: c.valueColumn.value,
                      type: c.type && c.type.value,
                    },
                  };
                }
                return {
                  columnName: c.columnName.value,
                  displayHeader: c.displayHeader,
                  type: c.type && c.type.value,
                  aggregationType: c.aggregationType && c.aggregationType.value,
                };
              }),
              fixHeight: t.fixHeight,
            })),
          lagTables:
            values.lagTables &&
            values.lagTables.map(lt => ({
              title: lt.title,
              reportView: lt.reportView && lt.reportView.value,
              pageSize: lt.pageSize,
              groupByColumns:
                lt.groupByColumns && lt.groupByColumns.map(col => col.value),
              divisionNameColumn:
                lt.divisionNameColumn && lt.divisionNameColumn.value,
              lagCalculations:
                lt.lagCalculations &&
                lt.lagCalculations.map(lc => ({
                  label: lc.label,
                  startDateColumn:
                    lc.startDateColumn && lc.startDateColumn.value,
                  endDateColumn: lc.endDateColumn && lc.endDateColumn.value,
                  businessDaysMode:
                    lc.businessDaysMode && lc.businessDaysMode.value,
                  businessDaysColumn:
                    lc.businessDaysColumn && lc.businessDaysColumn.value,
                })),
              additionalCountColumns:
                lt.additionalCountColumns &&
                lt.additionalCountColumns.map(cc => ({
                  label: cc.label,
                  countWhenColumn:
                    cc.countWhenColumn && cc.countWhenColumn.value,
                  countCondition: cc.countCondition && cc.countCondition.value,
                  countValue: cc.countValue,
                })),
              columns:
                lt.columns &&
                lt.columns.map(col => ({
                  type: col.type && col.type.value,
                  aggregationType: col.aggregationType && col.aggregationType.value,
                  sourceKey: col.sourceKey && col.sourceKey.value,
                  displayHeader: col.displayHeader,
                  showCustomNullText: col.showCustomNullText,
                  nullText: col.nullText,
                })),
            })),
          layout: values.layout,
        });

        const sliceContext = serializeSlice(values);

        const p = {
          reportConfigurationName: values.reportConfigurationName,
          reportConfigurationLabel: values.reportConfigurationName,
          reportConfigurationDescription: values.reportConfigurationDescription,
          reportConfigurationDefinition,
          ...sliceContext,
        };

        refAdmin[method](url, p)
          .then(() => toastSuccess())
          .then(() => history.push('/toolbox/configured-reports'))
          .catch(e => setErrors({ server: e }))
          .then(() => setSubmitting(false));
      }}
      render={({
 values, errors, handleSubmit, isSubmitting,
}) => (
  <form onSubmit={handleSubmit} noValidate>
    <Nav tabs>
      <NavItem>
        <NavLink
          onClick={() => setActiveTab(0)}
          className={classnames({ active: activeTab === 0 })}
          style={{ cursor: 'pointer' }}
          disabled={activeTab === 0}
        >
          Details
        </NavLink>
      </NavItem>

      <NavItem>
        <NavLink
          onClick={() => {
                  setActiveTab(1);
                  resizeHack();
                }}
          className={classnames({ active: activeTab === 1 })}
          style={{ cursor: 'pointer' }}
          disabled={activeTab === 1}
        >
          Edit Layout
        </NavLink>
      </NavItem>
    </Nav>

    <Card className="rounded-bottom rounded-0 border-top-0">
      <TabContent activeTab={activeTab}>
        <TabPane tabId={0}>
          <CardBody className="p-0">
            <fieldset disabled={isSubmitting}>
              {errors && <FormAlerts errors={errors} />}

              <Row form>
                <Col sm>
                  <Field
                    name="reportConfigurationName"
                    label="Report Name"
                    text="string used for side nav item configuration. cannot contain spaces"
                    required
                  />
                </Col>

                <Col sm>
                  <Field
                    name="reportConfigurationDescription"
                    label="Report Description"
                  />
                </Col>

                <Col sm>
                  <Field
                    name="reportView"
                    label="Base Report View"
                    text="name of database view that will be used for report"
                    type="select"
                    options={availableReports}
                    required
                  />
                </Col>
              </Row>
            </fieldset>

            <fieldset disabled={isSubmitting}>
              <CardTitle>Filters</CardTitle>

              <FieldArray
                name="filters"
                render={({ push, remove }) => (
                  <>
                    {values.filters &&
                            values.filters.map((f, i) => (
                              <React.Fragment key={i}>
                                <Button
                                  size="sm"
                                  outline
                                  color="danger"
                                  spear
                                  type="button"
                                  onClick={() => remove(i)}
                                >
                                  <FontAwesomeIcon
                                    icon="minus"
                                    className="mr-2"
                                  />
                                  Remove Filter
                                </Button>

                                <FilterForm
                                  index={i}
                                  availableRefDataModels={
                                    availableRefDataModels
                                  }
                                />

                                {i !== values.filters.length - 1 && <hr />}
                              </React.Fragment>
                            ))}

                    <Button
                      size="sm"
                      outline
                      color="tertiary"
                      spear
                      type="button"
                      onClick={() => push({})}
                    >
                      <FontAwesomeIcon icon="plus" className="mr-2" />
                      Add Filter
                    </Button>
                  </>
                      )}
              />
            </fieldset>

            <fieldset disabled={isSubmitting}>
              <CardTitle>Charts</CardTitle>

              <FieldArray
                name="charts"
                render={({ push, remove }) => (
                  <>
                    {values.charts &&
                            values.charts.map((c, i) => (
                              <React.Fragment key={i}>
                                <Button
                                  size="sm"
                                  outline
                                  color="danger"
                                  spear
                                  type="button"
                                  onClick={() => remove(i)}
                                >
                                  <FontAwesomeIcon
                                    icon="minus"
                                    className="mr-2"
                                  />
                                  Remove Chart
                                </Button>

                                <ChartForm
                                  index={i}
                                  availableReports={availableReports}
                                />

                                {i !== values.charts.length - 1 && <hr />}
                              </React.Fragment>
                            ))}

                    <Button
                      size="sm"
                      outline
                      color="tertiary"
                      spear
                      type="button"
                      onClick={() => push({ isNew: true })}
                    >
                      <FontAwesomeIcon icon="plus" className="mr-2" />
                      Add Chart
                    </Button>
                  </>
                      )}
              />
            </fieldset>

            <fieldset disabled={isSubmitting}>
              <CardTitle>Tables</CardTitle>

              <FieldArray
                name="tables"
                render={({ push, remove }) => (
                  <>
                    {values.tables &&
                            values.tables.map((t, i) => (
                              <React.Fragment key={i}>
                                <Button
                                  size="sm"
                                  outline
                                  color="danger"
                                  spear
                                  type="button"
                                  onClick={() => remove(i)}
                                >
                                  <FontAwesomeIcon
                                    icon="minus"
                                    className="mr-2"
                                  />
                                  Remove Table
                                </Button>

                                <TableForm
                                  index={i}
                                  availableReports={availableReports}
                                  columnTypeOptions={Object.keys(COLUMN_TYPE_MAP).map(v => ({
                                    label: startCase(v),
                                    value: v,
                                  }))}
                                />

                                {i !== values.tables.length - 1 && <hr />}
                              </React.Fragment>
                            ))}

                    <Button
                      size="sm"
                      outline
                      color="tertiary"
                      spear
                      type="button"
                      onClick={() => push({ pageSize: 500 })}
                    >
                      <FontAwesomeIcon icon="plus" className="mr-2" />
                      Add Table
                    </Button>
                  </>
                      )}
              />
            </fieldset>

            <fieldset disabled={isSubmitting}>
              <CardTitle>Lag Tables</CardTitle>

              <FieldArray
                name="lagTables"
                render={({ push, remove }) => (
                  <>
                    {values.lagTables &&
                            values.lagTables.map((lt, i) => (
                              <React.Fragment key={i}>
                                <Button
                                  size="sm"
                                  outline
                                  color="danger"
                                  spear
                                  type="button"
                                  onClick={() => remove(i)}
                                >
                                  <FontAwesomeIcon
                                    icon="minus"
                                    className="mr-2"
                                  />
                                  Remove Lag Table
                                </Button>

                                <LagTableForm
                                  index={i}
                                  availableReports={availableReports}
                                />

                                {i !== values.lagTables.length - 1 && <hr />}
                              </React.Fragment>
                            ))}

                    <Button
                      size="sm"
                      outline
                      color="tertiary"
                      spear
                      type="button"
                      onClick={() => push({ pageSize: 100 })}
                    >
                      <FontAwesomeIcon icon="plus" className="mr-2" />
                      Add Lag Table
                    </Button>
                  </>
                      )}
              />
            </fieldset>

            <fieldset>
              <CardTitle>Context Selector</CardTitle>

              <SliceFields />
            </fieldset>
          </CardBody>
        </TabPane>

        <TabPane tabId={1}>{values.layout && <LayoutEditor />}</TabPane>
      </TabContent>

      <CardFooter className="d-flex">
        <div className="ml-auto">
          <Button
            type="cancel"
            enabled={isSubmitting}
            tag={Link}
            to="/toolbox/configured-reports"
          >
            Cancel
          </Button>

          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Working ...' : 'Save'}
          </Button>
        </div>
      </CardFooter>
    </Card>
  </form>
      )}
    />
  );
};

ConfiguredReportsForm.propTypes = {
  data: PropTypes.shape().isRequired,
};

export default withRouter(ConfiguredReportsForm);
